import axios from 'axios';
import GML3 from 'ol/format/GML3';
import GeoJSON from 'ol/format/GeoJSON';
import WFS from 'ol/format/WFS';
import Intersects from 'ol/format/filter/Intersects';
import { toLonLat } from 'ol/proj';
import { validatedParcelLayer } from './ol/layers';
import { proj2154, proj3857 } from './ol/proj';
import { default as cascadedUnion } from './utils/geometry/cascaded-union';
import { default as getExtent } from './utils/geometry/extent';
import { featureCollection } from './utils/geometry/helpers';
import { default as getIntersection } from './utils/geometry/intersection';

const INPN_URL = 'http://ws.carmencarto.fr/WFS/119/fxx_inpn';

const INPN_FEATURE_TYPES = [
  {
    title: "Sites d'importance communautaire JOUE (ZSC,SIC)",
    name: 'Sites_d_importance_communautaire_JOUE__ZSC_SIC_',
  },
  {
    title: 'Zone Importante pour la Conservation des Oiseaux',
    name: 'ZICO',
  },
  {
    title: 'Zones de protection spéciale',
    name: 'Zones_de_protection_speciale',
  },
  {
    title: 'ZNIEFF 2',
    name: 'Znieff2',
  },
  {
    title: 'ZNIEFF 1',
    name: 'Znieff1',
  },
  {
    title: "Biens du patrimoine mondial de l'UNESCO",
    name: 'Bien_du_patrimoine_mondial_de_l_UNESCO',
  },
  {
    title: 'Réserves de biosphère',
    name: 'Reserves_de_la_biosphere',
  },
  {
    title: 'Réserves Naturelles Nationales',
    name: 'Reserves_naturelles_nationales',
  },
  {
    title: 'Sites RAMSAR',
    name: 'Sites_Ramsar',
  },
  {
    title: 'Terrains des Conservatoires des espaces naturels',
    name: 'Terrains_des_Conservatoires_des_espaces_naturels',
  },
  {
    title: 'Terrains du Conservatoire du Littoral',
    name: 'Terrains_du_Conservatoire_du_Littoral',
  },
  {
    title: 'Réserves nationales de chasse et faune sauvage',
    name: 'Reserves_nationales_de_chasse_et_faune_sauvage',
  },
  {
    title: 'Parcs nationaux',
    name: 'Parcs_nationaux',
  },
  {
    title: 'Réserves Intégrales de Parcs Nationaux',
    name: 'Reserves_Integrales_de_Parcs_Nationaux',
  },
  {
    title: 'Arrêtés de protection de biotope',
    name: 'Arretes_de_protection_de_biotope',
  },
  {
    title: 'Réserves biologiques',
    name: 'Reserves_biologiques',
  },
  {
    title: 'Parcs naturels régionaux',
    name: 'Parcs_naturels_regionaux',
  },
  {
    title: 'Reserves naturelles Corse',
    name: 'rnc',
  },
  {
    title: 'Arrêtés de protection de géotope',
    name: 'Arretes_de_protection_de_geotope',
  },
  {
    title: 'Géoparcs',
    name: 'Geoparcs',
  },
  {
    title: 'Réserves naturelles régionales',
    name: 'Reserves_naturelles_regionales',
  },
  {
    title: "Arrêtés de protection d'habitats naturels",
    name: 'Arretes_de_protection_d_habitats_naturels',
  },
  {
    title: 'Inventaire du patrimoine géologique',
    name: 'INPG',
  },
];

const ATLAS_PATRIMOINE_BASE_URL = 'http://atlas.patrimoines.culture.fr';
const ATLAS_PATRIMOINE_MS_PATH = 'cgi-bin/mapserv?map=/home/atlas-mapserver/production/var/data';

const ATLAS_PATRIMOINE_LABEL_KEY_LIST = [
  'SECT_LIB',
  'LIBELLE',
  'NOM',
  'appelation',
  'SITENAME',
  'IMMEUBLE',
  'IMM_LIB',
  'N_SITE',
  'NOM_SUP',
  'nom_site',
];

/**
 * Fetches feature types from the INPN.
 * @returns {Array} An array of feature types.
 */
async function getInpnFeatureTypes() {
  return INPN_FEATURE_TYPES.map((featType) => ({
    title: featType.title,
    name: featType.name,
    source: 'INPN',
    target: INPN_URL,
  }));
}

/**
 * Fetches feature types from the Atlas Patrimoine for a given parcel FeatureCollection.
 * @param {Object} parcelFc - A GeoJSON FeatureCollection object representing the parcels.
 * @returns {Promise<Array>} A promise that resolves to an array of feature types.
 */
async function getAtlasPatrimoineFeatureTypes(parcelFc) {
  // Group parcels by department
  const parcellesByDept = parcelFc.features.reduce((acc, obj) => {
    const groupKey = obj.properties.commune.substring(2, 0);
    return {
      ...acc,
      [groupKey]: (acc[groupKey] || []).concat(obj),
    };
  }, {});

  const altasPatrimoineFeatureTypes = [];

  for (const deptCode in parcellesByDept) {
    const parcellesList = parcellesByDept[deptCode];

    // Get the extent of the parcels in the department
    let [xmin, ymin, xmax, ymax] = getExtent(featureCollection(parcellesList));

    // Convert the extent coordinates to longitude and latitude
    [xmin, ymin] = toLonLat([xmin, ymin], proj2154);
    [xmax, ymax] = toLonLat([xmax, ymax], proj2154);

    // Fetch the feature types from the Atlas Patrimoine
    const res = await axios.get(`${import.meta.env.VITE_NEOSYLVA_API_URL}/wxs-proxy`, {
      params: {
        target: `${ATLAS_PATRIMOINE_BASE_URL}/geosource/srv/fr/rss.search`,
        themekeywords: 'Protection',
        westBL: xmin,
        eastBL: xmax,
        southBL: ymin,
        northBL: ymax,
        geoForm: 'BBOX',
        geoID: `DEP_${deptCode}`,
      },
      headers: {
        'neosylva-api-key': import.meta.env.VITE_NEOSYLVA_API_KEY,
      },
    });

    const resData = res?.data;

    if (!resData) {
      continue;
    }

    // Parse the XML response
    const parser = new DOMParser();
    const xmlRes = parser.parseFromString(resData, 'text/xml');

    // Get the protection items from the XML response
    const protectionItemsHtmlCollection = xmlRes.getElementsByTagName('item');

    // Process each protection item
    [...protectionItemsHtmlCollection].forEach((protectItem) => {
      const enclosure = protectItem.getElementsByTagName('enclosure')[0];

      if (enclosure) {
        let layerCode = enclosure.getAttribute('name');

        if (layerCode) {
          layerCode = layerCode.replace('layers=', '');

          // If the layer code is not already in the feature types array, add it
          if (!altasPatrimoineFeatureTypes.map((f) => f.name).includes(layerCode)) {
            const titleElem = protectItem.getElementsByTagName('title')[0];
            altasPatrimoineFeatureTypes.push({
              title: titleElem ? titleElem.textContent : '',
              name: layerCode,
              target: `${ATLAS_PATRIMOINE_BASE_URL}/${ATLAS_PATRIMOINE_MS_PATH}/${layerCode}/${layerCode}.map`,
            });
          }
        }
      }
    });
  }

  return altasPatrimoineFeatureTypes;
}

/**
 * Fetches all environmental feature types.
 * @param {Object} parcelFc - A GeoJSON FeatureCollection object representing the parcels.
 * @returns {Promise<Array>} A promise that resolves to an array of all environmental feature types.
 */
async function getAllEnvFeatureTypes(parcelFc) {
  const promisesArr = [getInpnFeatureTypes(), getAtlasPatrimoineFeatureTypes(parcelFc)];
  const allRes = await Promise.all(promisesArr);
  return allRes.flat();
}

/**
 * Fetches WFS features for a given feature type and geometry.
 * @param {Object} featureTypeInfo - An object containing information about the feature type.
 * @param {Object} geom - A JSTS Geometry object representing the geometry to intersect with.
 * @returns {Promise<Object>} A promise that resolves to the fetched WFS features.
 */
async function getWfsFeatures(featureTypeInfo, geom) {
  const projCode = proj2154.getCode();

  const body = new WFS().writeGetFeature({
    srsName: projCode,
    featureTypes: [featureTypeInfo.name],
    featureNS: 'http://mapserver.gis.umn.edu/mapserver',
    featurePrefix: 'feature',
    filter: new Intersects('msGeometry', geom, projCode),
  });

  return axios.post(`${import.meta.env.VITE_NEOSYLVA_API_URL}/wxs-proxy`, new XMLSerializer().serializeToString(body), {
    params: {
      target: featureTypeInfo.target,
    },
    headers: {
      'Content-Type': 'text/xml',
      'neosylva-api-key': import.meta.env.VITE_NEOSYLVA_API_KEY,
    },
  });
}

/**
 * Fetches environmental constraints.
 * @returns {Promise<Object>} A promise that resolves to an object containing the environmental constraints.
 */
export default async function getEnvContraintes() {
  // Create a new GeoJSON object from the validated parcel layer's features
  const parcellesFc = new GeoJSON({
    dataProjection: proj2154,
    featureProjection: proj3857,
  }).writeFeaturesObject(validatedParcelLayer.getSource().getFeatures());

  // Get all environmental feature types for the parcellesFc GeoJSON object
  const allFeaturesTypes = await getAllEnvFeatureTypes(parcellesFc);

  // Perform a cascaded union operation on the parcellesFc GeoJSON object
  const proprieteLimite = cascadedUnion(parcellesFc);

  // Read the geometry of the proprieteLimite object into a new GeoJSON object
  const proprieteOlGeom = new GeoJSON({
    dataProjection: proj2154,
    featureProjection: proj2154,
  }).readGeometry(proprieteLimite.geometry);

  // Map over all feature types
  const allPromisesList = allFeaturesTypes.map(async (featType) => {
    // Fetch WFS features for each feature type
    const res = await getWfsFeatures(featType, proprieteOlGeom);

    // Parse the XML response
    const resXml = new DOMParser().parseFromString(res.data, 'text/xml');

    // Read the features from the XML response
    const constraintesOlFeatures = new GML3()
      .readFeatures(resXml, {
        dataProjection: proj2154,
        featureProjection: proj2154,
      })
      .map((olFeature) => {
        // Find the label key in the feature keys
        const labelKey = ATLAS_PATRIMOINE_LABEL_KEY_LIST.find((labelKey) => olFeature.getKeys().includes(labelKey));

        // Set properties for the feature
        olFeature.setProperties({
          featureType: featType.title,
          label: labelKey ? olFeature.get(labelKey) : '',
          surface: olFeature.getGeometry().getArea(),
        });

        // Return the updated feature
        return olFeature;
      });

    // Convert the features to GeoJSON
    return new GeoJSON({
      dataProjection: proj2154,
      featureProjection: proj2154,
    }).writeFeaturesObject(constraintesOlFeatures).features;
  });

  // Wait for all promises in allPromisesList to resolve
  // allPromisesList contains promises that fetch environmental constraints
  const allContraintesList = await Promise.all(allPromisesList);

  // Flatten the array of environmental constraints
  const allContraintesFeaturesList = allContraintesList.flat();

  // Get the area of the property from the proprieteLimite object
  const surfacePropriete = proprieteLimite.properties.area;

  // Initialize a variable to hold the impacted property surface
  let surfaceProprieteImpactee;

  // Perform a cascaded union operation on the environmental constraints
  const contraintesUnion = cascadedUnion(featureCollection(allContraintesFeaturesList));

  // If there are no constraints, the impacted property surface is 0
  if (!contraintesUnion) {
    surfaceProprieteImpactee = 0;
  } else {
    // Get the intersection of the constraints union and the property
    const constraintsUnionIntersection = getIntersection(contraintesUnion, proprieteLimite);

    // Get the area of the intersection, but not more than the property area
    const contraintesUnionIntersectionSurface = Math.min(
      constraintsUnionIntersection.properties.area,
      surfacePropriete,
    );

    // Calculate the percentage of the property surface that is impacted
    // Round the result to the nearest whole number
    surfaceProprieteImpactee = Math.round((contraintesUnionIntersectionSurface / surfacePropriete) * 100);
  }

  // Map over all constraint features
  const contrainteFeaturesListInfos = allContraintesFeaturesList
    .map((contrainteFeature) => {
      // Initialize the total intersection surface area to 0
      let allIntersectionSurface = 0;
      // Initialize an object to store intersections
      const intersectionParcelles = {};

      // For each parcel feature
      parcellesFc.features.forEach((parcelleFeature) => {
        // Get the intersection of the constraint feature and the parcel feature
        const intersection = getIntersection(contrainteFeature, parcelleFeature);

        // Get the area of the intersection
        const intersectionSurface = intersection.properties.area;
        // If the intersection area is greater than 2
        if (intersectionSurface > 2) {
          // Add the intersection area to the total intersection surface area
          allIntersectionSurface += intersectionSurface;
          // Store the intersection in the intersections object
          intersectionParcelles[parcelleFeature.properties.id] = intersection;
        }
      });

      // Ensure that the total intersection surface area does not exceed the property area
      allIntersectionSurface = Math.min(allIntersectionSurface, surfacePropriete);

      // Get the feature type and label from the constraint feature properties
      const { featureType, label } = contrainteFeature.properties;

      // Calculate the percentage of the property surface that is impacted
      // Round the result to the nearest whole number
      const pourcentageSurfaceImpactee = Math.round((allIntersectionSurface / surfacePropriete) * 100);

      // Return an object containing the percentage of the surface impacted, the label, the feature type, and the intersections
      return {
        pourcentageSurfaceImpactee,
        label,
        featureType,
        intersectionParcelles,
      };
    })
    // Filter out any objects where the percentage of the surface impacted is 0
    .filter((c) => c.pourcentageSurfaceImpactee !== 0)
    // Sort the objects by feature type in ascending order
    .sort((a, b) => a.featureType.localeCompare(b.featureType));

  return {
    features: contrainteFeaturesListInfos,
    surfaceProprieteImpactee,
  };
}
